if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.loadingSpinner = this.querySelector('.loading__spinner');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.productInfo = this.closest('product-info');
        this.requiresSizeSelection = this.dataset.requiresSizeSelection === 'true';
        this.sizeOptionContainer = null;
        this.sizeRequiredRegion = null;
        this.sizeWarning = null;
        this.isSizeSelectionRequired = false;
        this.hasSizeSelectionSubmitAttempt = false;
        this.boundDocumentPointerDownHandler = this.onDocumentPointerDown.bind(this);
        this.boundVariantChangeHandler = this.onVariantControlsChange.bind(this);
        this.boundVariantChangedEventHandler = this.onVariantChangedEvent.bind(this);
        this.boundRequestedDesktopModeChangeHandler = this.onRequestedDesktopModeChange.bind(this);

        this.refreshSizeElements();
        this.syncSizeSelectionState();
        this.syncRequestedDesktopModeState();

        this.productInfo?.addEventListener('change', this.boundVariantChangeHandler);
        this.productInfo?.addEventListener('variant-picker:updated', this.boundVariantChangedEventHandler);
        document.addEventListener('pointerdown', this.boundDocumentPointerDownHandler);
        document.addEventListener('requested-desktop-mode:change', this.boundRequestedDesktopModeChangeHandler);
      }

      disconnectedCallback() {
        this.productInfo?.removeEventListener('change', this.boundVariantChangeHandler);
        this.productInfo?.removeEventListener('variant-picker:updated', this.boundVariantChangedEventHandler);
        document.removeEventListener('pointerdown', this.boundDocumentPointerDownHandler);
        document.removeEventListener('requested-desktop-mode:change', this.boundRequestedDesktopModeChangeHandler);
      }

      syncRequestedDesktopModeState() {
        if (!this.productInfo) return;
        const isRequestedDesktopMode = document.body?.classList.contains('is-requested-desktop-mode-on-mobile');
        this.productInfo.classList.toggle('is-requested-desktop-mode', isRequestedDesktopMode);
      }

      onRequestedDesktopModeChange() {
        this.syncRequestedDesktopModeState();
      }

      refreshSizeElements() {
        this.sizeOptionContainer = this.productInfo?.querySelector('[data-size-option]') || null;
        this.sizeRequiredRegion = this.productInfo?.querySelector('[data-size-required-region]') || null;
        this.sizeWarning = this.productInfo?.querySelector('[data-size-warning]') || null;
      }

      syncSizeSelectionState() {
        this.refreshSizeElements();
        const hasSelectedSize = this.hasSelectedSizeOption();
        this.isSizeSelectionRequired = Boolean(this.requiresSizeSelection && this.sizeOptionContainer && !hasSelectedSize);

        if (this.isSizeSelectionRequired) {
          this.submitButton?.removeAttribute('disabled');
        } else {
          this.hasSizeSelectionSubmitAttempt = false;
          this.clearSizeSelectionAttention();
        }

        this.updateSizeWarningVisibility();
      }

      onVariantControlsChange(event) {
        const target = event?.target;
        const isVariantOptionControl = Boolean(
          target?.matches?.('input[data-option-value-id], select.select__select')
        );
        if (!isVariantOptionControl) return;

        const isSizeOptionInput = Boolean(target?.closest?.('[data-size-option]'));

        if (isSizeOptionInput) {
          this.hasSizeSelectionSubmitAttempt = false;
        }

        this.syncSizeSelectionState();

        if (isSizeOptionInput) {
          this.clearSizeSelectionAttention();
          return;
        }

        window.requestAnimationFrame(() => this.syncSizeSelectionState());
      }

      onVariantChangedEvent() {
        window.requestAnimationFrame(() => this.syncSizeSelectionState());
      }

      showSizeSelectionAttention() {
        const target = this.sizeRequiredRegion || this.sizeOptionContainer;
        if (!target) return;

        target.classList.add('is-required-attention');
        this.updateSizeWarningVisibility();

        const firstSizeControl = this.sizeOptionContainer.querySelector(
          'input[data-size-option-input], select.select__select'
        );
        firstSizeControl?.focus();
      }

      clearSizeSelectionAttention() {
        this.sizeOptionContainer?.classList.remove('is-required-attention');
        this.sizeRequiredRegion?.classList.remove('is-required-attention');
        this.updateSizeWarningVisibility();
      }

      onDocumentPointerDown(event) {
        if (!this.sizeOptionContainer) return;
        if (this.sizeOptionContainer.contains(event.target)) return;
        this.clearSizeSelectionAttention();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        this.syncSizeSelectionState();
        if (this.isSizeSelectionRequired) {
          this.hasSizeSelectionSubmitAttempt = true;
          this.showSizeSelectionAttention();
          return;
        }

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.loadingSpinner?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.loadingSpinner?.classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        this.syncSizeSelectionState();

        if (this.isSizeSelectionRequired) {
          return;
        }

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      hasSelectedSizeOption() {
        if (!this.sizeOptionContainer) return false;
        const checkedSizeInput = this.sizeOptionContainer.querySelector('input[data-size-option-input]:checked');
        if (checkedSizeInput) return true;

        const sizeSelect = this.sizeOptionContainer.querySelector('select.select__select');
        return Boolean(sizeSelect && sizeSelect.value);
      }

      updateSizeWarningVisibility() {
        if (!this.sizeWarning) return;

        const shouldShowWarning = this.isSizeSelectionRequired && this.hasSizeSelectionSubmitAttempt;
        this.sizeWarning.classList.toggle('is-visible', shouldShowWarning);
      }
    }
  );
}
// cache bump 2026-05-11
